// Rental History Modal Component - แสดงประวัติการเช่าของแต่ละรายการ
import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { 
  RiTimeLine, 
  RiUserLine, 
  RiCalendarLine, 
  RiMoneyDollarCircleLine,
  RiFileTextLine,
  RiToolsLine,
  RiExchangeLine
} from 'react-icons/ri';

export interface RentalHistoryEntry {
  id: number;
  action: 'created' | 'updated' | 'status_changed' | 'payment' | 'contract_signed' | 'maintenance' | 'renewed' | 'cancelled';
  description: string;
  oldValue?: string;
  newValue?: string;
  amount?: number;
  performedBy: string;
  performedAt: string;
  notes?: string;
  attachments?: Array<{
    id: number;
    filename: string;
    url: string;
    type: string;
  }>;
}

export interface RentalHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  rentalId: number;
  rentalCode: string;
  history: RentalHistoryEntry[];
  loading?: boolean;
}

const RentalHistoryModal: React.FC<RentalHistoryModalProps> = ({
  isOpen,
  onClose,
  rentalId,
  rentalCode,
  history,
  loading = false
}) => {
  const [filteredHistory, setFilteredHistory] = useState<RentalHistoryEntry[]>(history);
  const [filterAction, setFilterAction] = useState<string>('all');

  useEffect(() => {
    if (filterAction === 'all') {
      setFilteredHistory(history);
    } else {
      setFilteredHistory(history.filter(entry => entry.action === filterAction));
    }
  }, [history, filterAction]);

  const getActionIcon = (action: RentalHistoryEntry['action']) => {
    switch (action) {
      case 'created':
        return <RiFileTextLine className="w-5 h-5 text-blue-500" />;
      case 'updated':
        return <RiFileTextLine className="w-5 h-5 text-yellow-500" />;
      case 'status_changed':
        return <RiExchangeLine className="w-5 h-5 text-purple-500" />;
      case 'payment':
        return <RiMoneyDollarCircleLine className="w-5 h-5 text-green-500" />;
      case 'contract_signed':
        return <RiFileTextLine className="w-5 h-5 text-indigo-500" />;
      case 'maintenance':
        return <RiToolsLine className="w-5 h-5 text-orange-500" />;
      case 'renewed':
        return <RiExchangeLine className="w-5 h-5 text-blue-600" />;
      case 'cancelled':
        return <RiTimeLine className="w-5 h-5 text-red-500" />;
      default:
        return <RiTimeLine className="w-5 h-5 text-gray-500" />;
    }
  };

  const getActionLabel = (action: RentalHistoryEntry['action']) => {
    switch (action) {
      case 'created':
        return 'สร้างรายการเช่า';
      case 'updated':
        return 'แก้ไขข้อมูล';
      case 'status_changed':
        return 'เปลี่ยนสถานะ';
      case 'payment':
        return 'การชำระเงิน';
      case 'contract_signed':
        return 'ลงนามสัญญา';
      case 'maintenance':
        return 'การซ่อมบำรุง';
      case 'renewed':
        return 'ต่อสัญญา';
      case 'cancelled':
        return 'ยกเลิก';
      default:
        return 'ไม่ระบุ';
    }
  };

  const getActionColor = (action: RentalHistoryEntry['action']) => {
    switch (action) {
      case 'created':
        return 'bg-blue-50 border-blue-200';
      case 'updated':
        return 'bg-yellow-50 border-yellow-200';
      case 'status_changed':
        return 'bg-purple-50 border-purple-200';
      case 'payment':
        return 'bg-green-50 border-green-200';
      case 'contract_signed':
        return 'bg-indigo-50 border-indigo-200';
      case 'maintenance':
        return 'bg-orange-50 border-orange-200';
      case 'renewed':
        return 'bg-blue-50 border-blue-200';
      case 'cancelled':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const renderHistoryEntry = (entry: RentalHistoryEntry, index: number) => {
    return (
      <div
        key={entry.id}
        className={`border rounded-lg p-4 ${getActionColor(entry.action)}`}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            {getActionIcon(entry.action)}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-900">
                {getActionLabel(entry.action)}
              </h4>
              <span className="text-xs text-gray-500">
                {formatDate(entry.performedAt)}
              </span>
            </div>
            
            <p className="text-sm text-gray-700 mb-2">
              {entry.description}
            </p>
            
            {/* แสดงการเปลี่ยนแปลงค่า */}
            {entry.oldValue && entry.newValue && (
              <div className="bg-white rounded p-2 mb-2 text-xs">
                <div className="flex items-center space-x-2">
                  <span className="text-red-600">เดิม: {entry.oldValue}</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-green-600">ใหม่: {entry.newValue}</span>
                </div>
              </div>
            )}
            
            {/* แสดงจำนวนเงิน */}
            {entry.amount && (
              <div className="text-sm font-medium text-green-600 mb-2">
                จำนวนเงิน: {entry.amount.toLocaleString()} บาท
              </div>
            )}
            
            {/* แสดงหมายเหตุ */}
            {entry.notes && (
              <div className="bg-white rounded p-2 mb-2">
                <p className="text-xs text-gray-600">
                  <strong>หมายเหตุ:</strong> {entry.notes}
                </p>
              </div>
            )}
            
            {/* แสดงไฟล์แนบ */}
            {entry.attachments && entry.attachments.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-600 mb-1">ไฟล์แนบ:</p>
                <div className="space-y-1">
                  {entry.attachments.map(attachment => (
                    <a
                      key={attachment.id}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary-600 hover:text-primary-800 underline block"
                    >
                      {attachment.filename}
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex items-center text-xs text-gray-500 mt-2">
              <RiUserLine className="w-3 h-3 mr-1" />
              ดำเนินการโดย: {entry.performedBy}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`ประวัติการเช่า - ${rentalCode}`}
      size="2xl"
    >
      <div className="space-y-4 relative">
        {/* Filter */}
        <div className="flex items-center space-x-4 pb-4 border-b bg-white relative z-10">
          <label className="text-sm font-medium text-gray-700 flex-shrink-0">
            กรองตามประเภท:
          </label>
          <div className="relative flex-shrink-0">
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 px-3 py-2 bg-white min-w-[150px] appearance-none relative z-20"
            >
            <option value="all">ทั้งหมด</option>
            <option value="created">สร้างรายการ</option>
            <option value="updated">แก้ไขข้อมูล</option>
            <option value="status_changed">เปลี่ยนสถานะ</option>
            <option value="cancelled">ยกเลิก</option>
            </select>
          </div>
        </div>

        {/* History Timeline */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-500">กำลังโหลดประวัติ...</p>
            </div>
          ) : filteredHistory.length > 0 ? (
            <div className="space-y-3">
              {filteredHistory.map((entry, index) => renderHistoryEntry(entry, index))}
            </div>
          ) : (
            <div className="text-center py-8">
              <RiTimeLine className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">ไม่มีประวัติ</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filterAction === 'all' 
                  ? 'ยังไม่มีประวัติการทำรายการ' 
                  : `ไม่มีประวัติประเภท ${getActionLabel(filterAction as any)}`
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RentalHistoryModal;